import React, { useEffect, useRef } from "react";
import { getImageFromAssetFolder } from "../../utils/assets";
import { resources } from "../Home/components/Resources";
import { openSocialMedia, SM } from "../../utils/socialMedia";

const appUrl = process.env.REACT_APP_ANDROID_APP_URL;

const screenshots = [
  { img: "1.png", reveal: "Tracking & Assessments" },
  { img: "2.png", reveal: "Performance Analytics" },
  { img: "3.png", reveal: "Performance Analytics" },
  { img: "4.png", reveal: "Performance Analytics" },
  { img: "5.png", reveal: "Actionable Feedback" },
  { img: "6.png", reveal: "Actionable Feedback" },
];

export default function LandingPage() {
  const refs = useRef([]);

  useEffect(() => {
    // smooth reveal
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
          }
        });
      },
      { threshold: 0.15 },
    );

    refs.current.forEach((el) => el && observer.observe(el));

    // cursor glow
    const glow = document.getElementById("cursorGlow");
    window.addEventListener("mousemove", (e) => {
      if (glow) {
        glow.style.left = e.clientX + "px";
        glow.style.top = e.clientY + "px";
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const Section = ({ children, index }) => (
    <div
      ref={(el) => (refs.current[index] = el)}
      className="opacity-0 translate-y-12 transition-all duration-700"
    >
      {children}
    </div>
  );

  return (
    <div className="bg-black text-white overflow-x-hidden font-sans">
      {/* CURSOR GLOW */}
      <div
        id="cursorGlow"
        className="fixed w-72 h-72 bg-indigo-500/20 blur-3xl rounded-full pointer-events-none -translate-x-1/2 -translate-y-1/2"
      ></div>

      {/* NAVBAR */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-lg bg-white/5 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between">
          <div className="flex items-center gap-3">
            <img
              width={100}
              src={getImageFromAssetFolder("logo_text_white.png")}
              alt="ZeroEqualOne"
              className="h-4 w-auto"
            />
          </div>
          <div className="flex items-center gap-6 text-sm opacity-80">
            <span
              className="hover:text-white transition cursor-pointer"
              onClick={() => window.open("/resources", "_self")}
            >
              Resources
            </span>
            <span
              className="hover:text-white transition cursor-pointer"
              onClick={() => window.open(appUrl)}
            >
              Download App
            </span>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="min-h-screen flex items-center justify-center relative overflow-hidden">
        {/* gradient mesh */}
        <div className="absolute inset-0">
          <div className="absolute w-[700px] h-[700px] bg-[radial-gradient(circle,_#6366f1,_transparent)] blur-3xl opacity-40 top-[-150px] left-[-150px] animate-pulse"></div>
          <div className="absolute w-[600px] h-[600px] bg-[radial-gradient(circle,_#ec4899,_transparent)] blur-3xl opacity-40 bottom-[-120px] right-[-120px] animate-pulse"></div>
          <div className="absolute w-[500px] h-[500px] bg-[radial-gradient(circle,_#22d3ee,_transparent)] blur-3xl opacity-30 top-[40%] left-[30%]"></div>
        </div>

        {/* noise overlay */}
        <div
          className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url('https://grainy-gradients.vercel.app/noise.svg')",
          }}
        />

        {/* floating UI cards */}
        <div className="absolute top-20 left-10 hidden md:block">
          <div className="p-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-xl animate-bounce">
            <p className="text-sm text-white/80">📊 Progress Tracking</p>
          </div>
        </div>

        <div className="absolute bottom-20 right-10 hidden md:block">
          <div className="p-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-xl animate-bounce delay-200">
            <p className="text-sm text-white/80">🧠 Smart Feedback</p>
          </div>
        </div>

        {/* main content */}
        <div className="relative text-center space-y-8 px-6 max-w-4xl">
          <h1 className="font-bold leading-tight tracking-tight text-[clamp(2rem,6vw,4rem)]">
            <span className="block opacity-0 animate-[fadeIn_1s_ease_forwards]">
              Personalized Guidance
            </span>
            <span className="block opacity-0 animate-[fadeIn_1s_ease_0.3s_forwards]">
              That Extends, Not Replaces,
            </span>
            <span className="block opacity-0 animate-[fadeIn_1s_ease_0.6s_forwards]">
              Learning
            </span>
          </h1>

          <p className="text-white/70 text-lg leading-relaxed opacity-0 animate-[fadeIn_1s_ease_0.9s_forwards]">
            ZeroEqualOne transforms study activity into clarity, feedback, and
            confidence — helping students learn smarter, teachers guide better,
            and institutions achieve more.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 opacity-0 animate-[fadeIn_1s_ease_1.2s_forwards]">
            <a href={appUrl} target="_blank">
              <button className="px-8 py-4 bg-white text-black rounded-full hover:scale-105 transition shadow-xl">
                Download App
              </button>
            </a>
            {/* <a href="/#how-it-works"> */}
            <button
              onClick={() => {
                const el = document.getElementById("how-it-works");
                if (el) {
                  const yOffset = -100; // adjust for navbar
                  const y =
                    el.getBoundingClientRect().top +
                    window.pageYOffset +
                    yOffset;

                  window.scrollTo({ top: y, behavior: "smooth" });
                }
              }}
              className="px-8 py-4 border border-white/30 rounded-full hover:bg-white/10 transition"
            >
              Learn How It Works
            </button>
            {/* </a> */}
          </div>

          {/* trust badges */}
          <div className="flex justify-center gap-6 pt-8 text-white/50 text-sm opacity-0 animate-[fadeIn_1s_ease_1.5s_forwards]">
            <span>Used by Students</span>
            <span>•</span>
            <span>Trusted by Teachers</span>
            <span>•</span>
            <span>Built for Institutions</span>
          </div>
        </div>

        {/* keyframes */}
        <style>{`
          @keyframes fadeIn {
            to { opacity: 1; transform: translateY(0); }
            from { opacity: 0; transform: translateY(20px); }
          }
        `}</style>
      </section>

      {/* PILLARS */}
      <section className="py-20 relative overflow-hidden">
        {/* subtle gradient bg */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-slate-900/60 to-black"></div>

        <div className="relative max-w-6xl mx-auto px-6">
          <Section index={0}>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-[clamp(1.8rem,4vw,3rem)] font-bold mb-4">
                The 3 Pillars of Smarter Learning
              </h2>
              <p className="text-white/70 leading-relaxed">
                ZEO doesn’t replace traditional learning. It strengthens it —
                with a system that turns student effort into meaningful growth.
              </p>
            </div>
          </Section>

          <div className="grid md:grid-cols-3 gap-10">
            {/* Pillar 1 */}
            <Section index={1}>
              <div className="group p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:scale-105 transition-all duration-500">
                <div className="w-12 h-12 mb-4 rounded-xl bg-indigo-500/20 flex items-center justify-center text-xl">
                  📊
                </div>
                <h3 className="text-xl font-semibold mb-2">
                  Tracking & Assessment
                </h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Seamlessly capture study activity and performance, without
                  adding extra workload.
                </p>
              </div>
            </Section>

            {/* Pillar 2 */}
            <Section index={2}>
              <div className="group p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:scale-105 transition-all duration-500">
                <div className="w-12 h-12 mb-4 rounded-xl bg-pink-500/20 flex items-center justify-center text-xl">
                  📈
                </div>
                <h3 className="text-xl font-semibold mb-2">Analysis</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Spot strengths, weaknesses, and trends with clarity-driven
                  insights.
                </p>
              </div>
            </Section>

            {/* Pillar 3 */}
            <Section index={3}>
              <div className="group p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:scale-105 transition-all duration-500">
                <div className="w-12 h-12 mb-4 rounded-xl bg-cyan-500/20 flex items-center justify-center text-xl">
                  🧠
                </div>
                <h3 className="text-xl font-semibold mb-2">
                  Actionable Feedback
                </h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Provide students, teachers, and institutions with steps that
                  actually improve outcomes.
                </p>
              </div>
            </Section>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS (FLOW) */}
      <section className="py-20 relative overflow-hidden" id="how-it-works">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-slate-900/60 to-black"></div>

        <div className="relative max-w-6xl mx-auto px-6">
          <Section index={20}>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-[clamp(1.8rem,4vw,3rem)] font-bold mb-4">
                How ZEO Works
              </h2>
              <p className="text-white/70 leading-relaxed">
                From effort to outcome — ZEO turns every student action into
                measurable growth.
              </p>
            </div>
          </Section>

          {/* flow */}
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-10">
            {/* connector line */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-[2px] bg-gradient-to-r from-indigo-500/30 via-pink-500/30 to-cyan-500/30"></div>

            {/* step 1 */}
            <Section index={21}>
              <div className="relative z-10 max-w-sm text-center">
                <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-indigo-500/20 flex items-center justify-center text-xl">
                  📊
                </div>
                <h3 className="text-xl font-semibold mb-2">Track</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Every study activity is recorded seamlessly — no extra burden.
                </p>
              </div>
            </Section>

            {/* step 2 */}
            <Section index={22}>
              <div className="relative z-10 max-w-sm text-center">
                <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-pink-500/20 flex items-center justify-center text-xl">
                  📈
                </div>
                <h3 className="text-xl font-semibold mb-2">Analyze</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Patterns, strengths, and weaknesses become visible.
                </p>
              </div>
            </Section>

            {/* step 3 */}
            <Section index={23}>
              <div className="relative z-10 max-w-sm text-center">
                <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-cyan-500/20 flex items-center justify-center text-xl">
                  🧠
                </div>
                <h3 className="text-xl font-semibold mb-2">Feedback</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Actionable guidance delivered to students, teachers, and
                  institutions.
                </p>
              </div>
            </Section>
          </div>
        </div>
      </section>

      {/* WHO IT’S FOR */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-slate-900/60 to-black"></div>

        <div className="relative max-w-6xl mx-auto px-6">
          <Section index={30}>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-[clamp(1.8rem,4vw,3rem)] font-bold mb-4">
                Who It’s For
              </h2>
              <p className="text-white/70 leading-relaxed">
                Not every learner, teacher, or institute needs more content.
                They need clarity, structure, and guidance. That’s where ZEO
                fits in.
              </p>
            </div>
          </Section>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Students */}
            <Section index={31}>
              <div className="p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:scale-105 transition-all duration-500">
                <div className="w-12 h-12 mb-4 rounded-xl bg-indigo-500/20 flex items-center justify-center text-xl">
                  🎓
                </div>
                <h3 className="text-xl font-semibold mb-4">Students</h3>
                <ul className="space-y-2 text-white/70 text-sm">
                  <li>• Struggling with self-study overwhelm.</li>
                  <li>
                    • Preparing for exams but unsure if effort is paying off.
                  </li>
                  <li>• Wanting clear, data-backed feedback.</li>
                </ul>
              </div>
            </Section>

            {/* Teachers */}
            <Section index={32}>
              <div className="p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:scale-105 transition-all duration-500">
                <div className="w-12 h-12 mb-4 rounded-xl bg-pink-500/20 flex items-center justify-center text-xl">
                  👩‍🏫
                </div>
                <h3 className="text-xl font-semibold mb-4">
                  Teachers & Mentors
                </h3>
                <ul className="space-y-2 text-white/70 text-sm">
                  <li>• Guide smarter, not harder.</li>
                  <li>• Visibility beyond just marks.</li>
                  <li>• Evidence-based insights for personalization.</li>
                </ul>
              </div>
            </Section>

            {/* Institutes */}
            <Section index={33}>
              <div className="p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:scale-105 transition-all duration-500">
                <div className="w-12 h-12 mb-4 rounded-xl bg-cyan-500/20 flex items-center justify-center text-xl">
                  🏫
                </div>
                <h3 className="text-xl font-semibold mb-4">
                  Institutes & Coaching Centers
                </h3>
                <ul className="space-y-2 text-white/70 text-sm">
                  <li>• Improve outcomes with same resources.</li>
                  <li>• Structured feedback loops.</li>
                  <li>• Higher satisfaction, retention, success.</li>
                </ul>
              </div>
            </Section>
          </div>

          {/* highlight */}
          <Section index={34}>
            <div className="mt-16 text-center">
              <div className="inline-block px-6 py-4 rounded-2xl bg-gradient-to-r from-indigo-500/20 via-pink-500/20 to-cyan-500/20 backdrop-blur-xl border border-white/10">
                <p className="text-white/90 text-lg font-medium">
                  🚀 ZEO is for anyone who believes education should be about
                  progress, not guesswork.
                </p>
              </div>
            </div>
          </Section>
        </div>
      </section>

      {/* PORTFOLIO (IMAGE BLEND) */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/60 to-black"></div>

        <div className="relative max-w-6xl mx-auto grid md:grid-cols-6 gap-10 px-6">
          {screenshots.map(({ img, reveal }, i) => (
            <Section key={i} index={i + 9}>
              <div className="relative rounded-3xl overflow-hidden group">
                <img
                  src={`${getImageFromAssetFolder(img, "screenshots/")}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
                />
                <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                  <h3 className="text-xl text-center">{reveal}</h3>
                </div>
              </div>
            </Section>
          ))}
        </div>

        <Section index={46}>
          <div className="mt-16 text-center">
            <div className="mt-10 text-center">
              <div className="inline-block px-6 py-4 rounded-2xl bg-gradient-to-r from-indigo-500/20 via-pink-500/20 to-cyan-500/20 backdrop-blur-xl border border-white/10">
                <p className="text-white/90 text-lg font-medium">
                  🚀 Data shows you where you stand. Mentors show you how to
                  move forward — with clear feedback and strategies.
                </p>
              </div>
            </div>
          </div>
        </Section>
      </section>

      {/* WHY TRUST */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-slate-900/60 to-black"></div>

        <div className="relative max-w-6xl mx-auto px-6">
          <Section index={60}>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-[clamp(1.8rem,4vw,3rem)] font-bold mb-4">
                Why Trust ZeroEqualOne?
              </h2>
              <p className="text-white/70 leading-relaxed">
                Built by learners, mentors, and engineers who’ve seen both sides
                of the struggle. ZeroEqualOne is designed not as another content
                dump, but as a system to truly improve learning outcomes.
              </p>
            </div>
          </Section>

          <div className="grid md:grid-cols-3 gap-8">
            <Section index={61}>
              <div className="p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:scale-105 transition-all duration-500">
                <div className="text-2xl mb-4">🌱</div>
                <h3 className="text-xl font-semibold mb-2">
                  Born from Real Struggles
                </h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Inspired by the founder’s journey from a small village to
                  becoming a self-taught engineer — ZEO exists to solve problems
                  students actually face.
                </p>
              </div>
            </Section>

            <Section index={62}>
              <div className="p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:scale-105 transition-all duration-500">
                <div className="text-2xl mb-4">📊</div>
                <h3 className="text-xl font-semibold mb-2">
                  Backed by Data & Insights
                </h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Every feature is rooted in real classroom and coaching
                  experiences, ensuring advice is always practical and
                  personalized.
                </p>
              </div>
            </Section>

            <Section index={63}>
              <div className="p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:scale-105 transition-all duration-500">
                <div className="text-2xl mb-4">🚀</div>
                <h3 className="text-xl font-semibold mb-2">Built for Scale</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Designed to support individual learners, teachers, and
                  institutions — helping everyone achieve more with the same
                  resources.
                </p>
              </div>
            </Section>
          </div>
        </div>
      </section>

      {/* ARTICLES */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-slate-900/60 to-black"></div>

        <div className="relative max-w-6xl mx-auto px-6">
          <Section index={70}>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-[clamp(1.8rem,4vw,3rem)] font-bold mb-4">
                Insights that Shape ZEO
              </h2>
              <p className="text-white/70 leading-relaxed">
                Our approach to learning is grounded in philosophy and practice.
                Explore the ideas behind ZEO — and see how we’re rethinking
                education.
              </p>
            </div>
          </Section>

          <div className="grid md:grid-cols-2 gap-8">
            {resources
              .filter((it) => it.showOnHomaPage)
              .map((article, i) => (
                <Section key={i} index={71 + i}>
                  <div className="group p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:scale-[1.02] transition-all duration-500 cursor-pointer">
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-white">
                      {article.title}
                    </h3>
                    <p className="text-white/70 text-sm leading-relaxed mb-6">
                      {article.description}
                    </p>
                    <a href={`/resources/${article.slug}`}>
                      <span className="text-sm text-indigo-400 group-hover:translate-x-1 inline-block transition">
                        Read More →
                      </span>
                    </a>
                  </div>
                </Section>
              ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 text-center relative overflow-hidden">
        {/* ambient gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/50 via-purple-900/50 to-pink-900/50"></div>
        <div
          className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url('https://grainy-gradients.vercel.app/noise.svg')",
          }}
        />

        <Section index={80}>
          <div className="relative max-w-4xl mx-auto px-6">
            <h2 className="font-bold leading-tight tracking-tight text-[clamp(2.2rem,6vw,4rem)] mb-4">
              Don’t Just Learn More.
              <br />
              <span className="text-white/90">Learn Smarter with ZEO.</span>
            </h2>

            <p className="text-white/80 text-lg leading-relaxed max-w-2xl mx-auto mb-10">
              ZEO is not another content platform. It’s a system built to give
              students clarity, structure, and confidence — while empowering
              teachers and institutions to guide smarter.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={appUrl} target="_blank">
                <button className="px-10 py-5 bg-white text-black rounded-full font-semibold hover:scale-105 transition shadow-2xl">
                  🎓 Download App
                </button>
              </a>
              <a
                href="https://wa.me/919471664452?text=Hello%21%20I%E2%80%99d%20like%20to%20request%20a%20collaboration%20for%20the%20ZEO%20App.%20Looking%20forward%20to%20hearing%20from%20you."
                target="_blank"
              >
                <button className="px-10 py-5 border border-white/30 rounded-full font-semibold hover:bg-white/10 transition">
                  🤝 Collaborate With Us
                </button>
              </a>
            </div>

            {/* subtle underline glow */}
            <div className="mt-12 mx-auto h-[2px] w-40 bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
          </div>
        </Section>
      </section>

      {/* FOOTER */}
      <footer className="py-16 bg-black border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            <div>
              <img
                width={100}
                src={getImageFromAssetFolder("logo_text_white.png")}
                alt="ZeroEqualOne"
                className="h-4 w-auto mb-2"
              />
              <h3 className="text-xl font-semibold mb-4">
                Learn Beyond The Obvious
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Turning effort into impact. Smarter learning, guided by clarity
                and data.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li>
                  <a href="/about-us">About Us</a>
                </li>
                <li>
                  <a href="/legal/privacy-policies">Privacy Policies</a>
                </li>
                <li>
                  <a href="/legal/terms-of-use">Terms & Conditions</a>
                </li>
                <li>
                  <a href="/legal/user-policies">User Policies</a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Product & Services</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li>Syllabus Tracking</li>
                <li>Supplementary Contents</li>
                <li>Support & Guidance</li>
                <li>Career Counseling</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Connect</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li
                  className="cursor-pointer"
                  onClick={() => openSocialMedia(SM.FB)}
                >
                  Facebook
                </li>
                <li
                  className="cursor-pointer"
                  onClick={() => openSocialMedia(SM.IN)}
                >
                  LinkedIn
                </li>
                <li
                  className="cursor-pointer"
                  onClick={() => openSocialMedia(SM.INSTA)}
                >
                  Instagram
                </li>
                <li
                  className="cursor-pointer"
                  onClick={() => openSocialMedia(SM.X)}
                >
                  X (Twitter)
                </li>
                <li
                  className="cursor-pointer"
                  onClick={() => openSocialMedia(SM.YT)}
                >
                  YouTube
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 text-center text-sm text-white/50">
            Copyright © 2024 ZeroEqualOne: Learn Beyond The Obvious. All rights
            reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
